import React, { useCallback, useMemo } from "react";
import * as Scrivito from "scrivito";
import cx from "classnames";
import { ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import NiceModal, { useModal } from "@ebay/nice-modal-react";

import { updateTopologyItemOrderCode } from "utils";
import { useStateLoaderContext } from "providers";

import { useTranslate } from "hooks";
import {
  useIsFetching,
  useTopology,
  useWizardActions,
} from "objs/_ConfiguratorObjs/Wizard/stores/wizardStore";
import { ProductConfiguratorComponent } from "components/_ProductConfigurator";
import { Button } from "components/Button";
import { ModalWrapper } from "components/ModalWrapper";
import { useProductConfiguratorStore } from "components/_ProductConfigurator/store/ProductConfiguratorStore";
import {
  productToWizardOrderCodeAdapter,
  wizardOrderCodeToProductApiAdapter,
} from "objs/_ConfiguratorObjs/Wizard/ProductConfiguratorModal/orderCodeWizardAdapter";
import s from "./ConfiguratorModal.module.scss";

export const ProductConfiguratorModal = NiceModal.create(
  Scrivito.connect(
    ({
      id,
      orderCode,
      dummy,
      type,
      seriesId,
      wizardType,
      handleBackAction,
      groupPosition,
      selectedAccessories,
      forceLoadDefault = false,
    }) => {
      const translate = useTranslate();
      const modal = useModal();
      const isOrderCodeLoading = useProductConfiguratorStore.use.isLoading();
      const requestParams = useProductConfiguratorStore.use.requestParams();
      const { product, productImageList } =
        useProductConfiguratorStore.use.productData() || {
          product: null,
          productImageList: [],
        };

      const initOrderCode = useMemo(
        () =>
          wizardOrderCodeToProductApiAdapter(orderCode, selectedAccessories),
        [orderCode, selectedAccessories],
      );

      const isFetchingTopologyAction = useIsFetching();
      const { setTopology, invokeTopologyAction } = useWizardActions();
      const topology = useTopology();

      const handleConfigureProduct = useCallback(() => {
        const adaptedData = productToWizardOrderCodeAdapter(
          initOrderCode,
          product,
          requestParams,
        );

        setTopology(
          updateTopologyItemOrderCode({
            topology,
            id,
            pimId:
              productImageList.productImages.find(
                (img) => img.typ === "ProductPhoto",
              )?.pimId || null,
            ...adaptedData,
          }),
        );

        invokeTopologyAction({
          action: "configureComponent",
        }).then(() => {
          modal.remove();
        });
      }, [
        initOrderCode,
        product,
        requestParams,
        setTopology,
        topology,
        id,
        productImageList.productImages,
        invokeTopologyAction,
        modal,
      ]);

      const handleClose = useCallback(() => {
        modal.remove();
      }, [modal]);

      const backToProductsOverview = useCallback(() => {
        handleBackAction();
        modal.remove();
      }, [handleBackAction, modal]);

      const { isFetching } = useStateLoaderContext();

      return (
        <ModalWrapper
          isOpen={modal.visible}
          onClosed={handleClose}
          toggle={handleClose}
          size="xl"
          isLoading={isFetching && !orderCode?.seriesId}
          contentClassName="mh-100"
        >
          <div className="position-relative d-flex flex-column">
            <div className={cx("d-flex flex-column", s.configuratorModalInner)}>
              <ModalHeader
                toggle={modal.remove}
                tag="div"
                className={cx("pb-0 head")}
              >
                {modal.visible ? (
                  <Button
                    data-testid="ProductConfiguratorModal_back-btn"
                    color="default"
                    iconPrefix="solid"
                    className="px-0 mb-1"
                    icon="angle-left"
                    onClick={backToProductsOverview}
                  >
                    {translate("BACK")}
                  </Button>
                ) : null}
              </ModalHeader>
              <ModalBody className="flex-grow-1 px-0 py-0">
                {modal.visible && (
                  <ProductConfiguratorComponent
                    nodeId={id}
                    isModalView={true}
                    isWizardView={true}
                    configuredSeriesIdOf={orderCode.seriesId ?? seriesId}
                    wizardType={wizardType}
                    dummyType={dummy}
                    type={type}
                    groupPosition={groupPosition}
                    forceLoadDefault={forceLoadDefault}
                  />
                )}
              </ModalBody>
            </div>
            <ModalFooter className=" w-100 bg-white d-flex align-items-center py-2_5">
              <Button
                data-testid="ProductConfiguratorModal_update-configuration-btn"
                disabled={
                  !product?.orderCode ||
                  isFetchingTopologyAction ||
                  isOrderCodeLoading
                }
                isLoading={
                  !product?.orderCode ||
                  isFetchingTopologyAction ||
                  isOrderCodeLoading
                }
                className="m-0"
                onClick={handleConfigureProduct}
              >
                {translate("PRODUCT_CONFIGURATOR_UPDATE_CONFIGURATION")}
              </Button>
            </ModalFooter>
          </div>
        </ModalWrapper>
      );
    },
  ),
);
